import type { AuthMethodsDto } from '@tk/shared';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router';
import Logo from '../components/Logo';
import SovellusMerkki from '../components/SovellusMerkki';
import { api } from '../lib/api';
import { useKaannos, type Avain } from '../lib/i18n';
import { useBranding } from '../lib/queries';
import { otsikkoRivi } from '../lib/tyylit';
import { useMeQuery } from '../lib/session';
import KehitysKirjautuminen from './KehitysKirjautuminen';
import PaikallinenKirjautuminen from './PaikallinenKirjautuminen';

/** Palvelimen ohjaama virhetunnus osoitteessa → sanaston avain. */
const VIRHEAVAIMET: Record<string, Avain> = {
  'kirjautuminen-keskeytyi': 'kirjautuminen.virheKeskeytyi',
  'kirjautuminen-vanhentui': 'kirjautuminen.virheVanhentui',
  'kirjautuminen-epaonnistui': 'kirjautuminen.virheEpaonnistui',
  // Sovelluksen oma ohjaus, kun istunto päättyy kesken käytön (lib/session.tsx).
  'istunto-paattyi': 'kirjautuminen.virheIstuntoPaattyi',
};

export default function KirjauduSivu() {
  const [params] = useSearchParams();
  const { data, isPending } = useMeQuery();
  const { data: tunnus } = useBranding();
  const { t } = useKaannos();

  /**
   * Kirjautumistavat ovat asennuksen konfiguraatiota, eivät kenenkään
   * tietoa, joten ne haetaan ilman istuntoa. Oletus ennen vastausta on
   * pelkkä Microsoft: se on tuotannon tapaus, eikä salasanakenttä ehdi
   * vilahtaa niille, joilla sitä ei ole.
   */
  const { data: tavat } = useQuery<AuthMethodsDto>({
    queryKey: ['auth-methods'],
    queryFn: () => api.get<AuthMethodsDto>('/auth/methods'),
    staleTime: Infinity,
  });

  const paluu = params.get('paluu');
  const virhe = params.get('virhe');

  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-teksti-vaimea text-sm">{t('yleinen.ladataan')}</p>
      </div>
    );
  }

  if (data) {
    return <Navigate to={paluu ?? '/kalenteri'} replace />;
  }

  const microsoft = tavat?.microsoft ?? true;
  const paikallinen = tavat?.local ?? false;

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* Brändipaneeli: sama kansi kuin esittelyssä
        (docs/esittely/devikone-platform.html). Tumma teemasta riippumatta,
        koska se on Devikone Platformin tunnus eikä osa käyttöliittymää —
        yrityksen oma tunnus on kortissa oikealla. Kapealla näytöllä
        paneeli jää pois, eikä kirjautuminen menetä sen mukana mitään. */}
      <aside className="bg-muste hidden flex-col justify-between p-12 text-white lg:flex">
        <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.16em] text-white/65">
          <span className="bg-brandi size-2 rounded-full" aria-hidden="true" />
          {t('sovellus.nimi')}
        </p>
        <div className="max-w-xl">
          <p className="font-heading text-5xl font-black leading-[1.02] tracking-tight xl:text-6xl">
            {t('kirjautuminen.iskulause')}
            <span className="text-brandi">.</span>
          </p>
          <p className="mt-6 max-w-md text-lg font-light leading-snug text-white/80">
            {t('kirjautuminen.kuvaus')}
          </p>
        </div>
        <SovellusMerkki className="size-28 self-end text-white" />
      </aside>

      <div className="flex items-center justify-center px-4 py-10">
        <div className="border-reuna bg-pinta-korotettu w-full max-w-sm rounded-xl border p-8 shadow-sm">
          <Logo className="h-9 w-auto max-w-48 object-contain" />
          <div className="mt-5 flex items-center gap-2.5">
            <SovellusMerkki className="text-teksti size-7 shrink-0" />
            <h1 className="text-teksti text-xl font-semibold tracking-tight">
              {t('sovellus.nimi')}
            </h1>
          </div>
          {/* Nimi tunnuksen lisäksi eikä sen sijaan: räätälöity tunnus voi
          olla pelkkä kuviomerkki, josta yritystä ei tunnista. */}
          <p className="text-teksti-vaimea mt-1 text-sm">{tunnus?.brandName ?? ''}</p>

          {virhe && (
            <p
              role="alert"
              className="border-yli/40 bg-yli/10 text-teksti mt-6 rounded-md border px-3 py-2 text-sm"
            >
              {t(VIRHEAVAIMET[virhe] ?? 'kirjautuminen.virheYleinen')}
            </p>
          )}

          {microsoft && (
            <>
              <a
                href="/auth/login"
                className="bg-brandi hover:bg-brandi-syva mt-6 flex w-full items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium text-white transition-colors"
              >
                {t('kirjautuminen.microsoft')}
              </a>

              <p className="text-teksti-vaimea mt-4 text-xs leading-relaxed">
                {t('kirjautuminen.ohje')}
              </p>
            </>
          )}

          {paikallinen && (
            <div className={microsoft ? 'border-reuna mt-6 border-t pt-6' : 'mt-6'}>
              {microsoft && (
                <p className={`${otsikkoRivi} mb-3`}>{t('kirjautuminen.salasanaOtsikko')}</p>
              )}
              <PaikallinenKirjautuminen paluu={paluu ?? '/kalenteri'} />
              <p className="text-teksti-vaimea mt-4 text-xs leading-relaxed">
                {t('kirjautuminen.paikallinenOhje')}
              </p>
            </div>
          )}

          {/* Pudotetaan pois tuotantokäännöksestä kokonaan. */}
          {import.meta.env.DEV && <KehitysKirjautuminen paluu={paluu ?? '/kalenteri'} />}
        </div>
      </div>
    </div>
  );
}
